/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { PREVIEW_TITLE_MAX_LENGTH, STAR_RATING_MAX_STARS } from '../../../config';
import { DeviceFeatures } from '../../../core/device';
import { BrandingUiInfoKeys } from '../../../core/entities/BrandingUiInfoKeys';
import { ServicesClientImpl } from '../../../core/interactors';
import { RecordingRestriction } from '../../../core/usecases';
import { RestrictionMessage } from '../../common';
import { OfferPreviewFormatter } from '../../common/OfferPreviewFormatter';
import { ImageLoader } from '../../ImageLoader';
import { ImageLoaderTypes } from '../../ImageLoader/ImageLoaderTypes';
import { RECORDING_TYPE, RecordingStatusIcon } from '../RecordingStatusIcon';
import { PreviewPaneProps } from './PreviewPaneProps';

import './PreviewPane.scss';

const CONTENT_VISIBILITY = {
    HIDE: 'hide-visibility',
    UNHIDE: 'unhide-visibility',
};
const PreviewPane: FunctionComponent<PreviewPaneProps> = ({
    offerPreview,
    hideMetadata,
    channel,
    selectedCell,
    horizontalPosition,
    verticalPosition,
}) => {
    const oOfferPreviewFormatter: OfferPreviewFormatter = new OfferPreviewFormatter(
        offerPreview,
        hideMetadata,
    );
    const services = new ServicesClientImpl();
    const currentTitle =
        selectedCell?.title ?? (channel ? `${channel.callSign} ${channel.channelNumber}` : '');
    const initialTitleTruncate = (currentTitle?.length || 0) > PREVIEW_TITLE_MAX_LENGTH;
    const initialTitleClass = initialTitleTruncate ? ' adjust-font' : '';
    const [previewBodyClass, setPreviewBodyClass] = useState<string>(CONTENT_VISIBILITY.HIDE);
    const [completeOfferPreviewReceived, setCompleteOfferPreviewReceived] = useState(false);
    const titleDivRef = useRef<HTMLDivElement>(null);
    const movieYearDiv = oOfferPreviewFormatter.hasMoviePreview() ? (
        <div className="movie-year">{oOfferPreviewFormatter.getMovieYear()}</div>
    ) : null;
    const newIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.NEW_ICON);
    const isNewDiv = oOfferPreviewFormatter.hasNewIcon() ? (
        <img src={newIcon?.imageUrl} className={`is-new ${newIcon?.imageType}`} />
    ) : null;

    const descriptionDivRef = useRef<HTMLDivElement>(null);
    const [previewDescriptionClass, setPreviewDescriptionClass] = useState<string>('');
    const [truncatePreviewDescription, setTruncatePreviewDescription] = useState<boolean>(false);
    const ccVideoResolutionText = oOfferPreviewFormatter.getCcVideoResolution();

    const contentInfoDiv = (
        <div className="content-info">
            {ccVideoResolutionText.length > 0 ? (
                <div className="cc_stream">{ccVideoResolutionText}</div>
            ) : null}
            <div className="category-first-aired">
                {oOfferPreviewFormatter.getCategoryAndFirstAired()}
            </div>
        </div>
    );

    const internalRatingsDiv = oOfferPreviewFormatter
        .getInternalRatings()
        .map((ratingName, index) => {
            return (
                <span className="internal-ratings" key={index}>
                    <span className="rating-text">{ratingName}</span>
                </span>
            );
        });

    const starRating = oOfferPreviewFormatter.getStarRating();
    const starRatingDiv = oOfferPreviewFormatter.hasStarRating() ? (
        <span className="star-rating">
            {new Array(Math.floor(starRating)).fill(0).map((_, index) => {
                return <span className="star-icon full" key={index}></span>;
            })}
            {starRating > Math.floor(starRating) ? <span className="star-icon half"></span> : null}
            {new Array(STAR_RATING_MAX_STARS - Math.floor(starRating)).fill(0).map((_, index) => {
                return <span className="star-icon empty" key={index}></span>;
            })}
        </span>
    ) : null;

    const contentAiringInfo = (
        <div className="content-airing-info">
            {oOfferPreviewFormatter.getContentAiringDateTime()}
            {internalRatingsDiv}
            {starRatingDiv}
        </div>
    );

    if (oOfferPreviewFormatter.isAvailableInSocu()) {
        // TODO: add logging
    }

    const recordingStatus =
        offerPreview.contentPreview?.recordingStatus ===
        RECORDING_TYPE.GuidePreviewCompleteRecording;

    const providerImagesDiv = DeviceFeatures.isSocuEnabled() ? (
        <div className="provider-images">
            {recordingStatus ? (
                <RecordingStatusIcon
                    type={RECORDING_TYPE.GuidePreviewCompleteRecording}
                    styleClass={ImageLoaderTypes.PROVIDER_IMAGE_PREVIEW_ICON}
                />
            ) : null}
            {oOfferPreviewFormatter
                .getProvidersImageUrls()
                ?.map((url: string, index: number) => (
                    <ImageLoader
                        key={index}
                        src={url}
                        imageType={ImageLoaderTypes.PROVIDER_IMAGE_PREVIEW_ICON}
                    />
                ))}
        </div>
    ) : null;

    useEffect(() => {
        if (!offerPreview.contentPreview?.title) {
            return;
        }
        setCompleteOfferPreviewReceived(true);
    }, [offerPreview]);

    useEffect(() => {
        if (!completeOfferPreviewReceived) return;

        if (titleDivRef.current) {
            if (titleDivRef.current.clientHeight < titleDivRef.current.scrollHeight) {
                setPreviewDescriptionClass('reduce-height ');
            } else {
                setPreviewDescriptionClass('');
            }
        }
        if (descriptionDivRef.current) {
            if (descriptionDivRef.current.clientHeight < descriptionDivRef.current.scrollHeight) {
                setTruncatePreviewDescription(true);
            } else {
                setTruncatePreviewDescription(false);
            }
            setPreviewBodyClass(CONTENT_VISIBILITY.UNHIDE);
        }
    }, [completeOfferPreviewReceived]);

    useEffect(() => {
        if (completeOfferPreviewReceived) {
            setCompleteOfferPreviewReceived(false);
            setPreviewBodyClass(CONTENT_VISIBILITY.HIDE);
        }
    }, [horizontalPosition, verticalPosition]);

    return (
        <React.Fragment>
            {completeOfferPreviewReceived ? (
                <div ref={titleDivRef} className={`preview-title`} id="preview-formatted-title'">
                    {oOfferPreviewFormatter.getPreviewTitle()}
                    {oOfferPreviewFormatter.getStreamType() ? (
                        <div
                            className={`preview-stream-type-icon ${oOfferPreviewFormatter.getStreamType()}`}
                        ></div>
                    ) : null}
                    <div
                        className={`title-attributes ${
                            previewDescriptionClass !== '' ? 'truncate' : ''
                        }`}
                    >
                        {previewDescriptionClass !== '' ? (
                            <div className="transparent-effect">&nbsp;</div>
                        ) : null}
                        <div className="other-details">
                            {movieYearDiv}
                            {isNewDiv}
                        </div>
                    </div>
                </div>
            ) : (
                <div className={`preview-title${initialTitleClass}`}>
                    {currentTitle}
                    <div className={`title-attributes ${initialTitleTruncate ? 'truncate' : ''}`}>
                        {initialTitleTruncate ? (
                            <div className="transparent-effect">&nbsp;</div>
                        ) : null}
                        <div className="other-details">
                            {selectedCell?.movieYear ? (
                                <div className="movie-year">({selectedCell?.movieYear})</div>
                            ) : null}
                            {selectedCell?.isNew ? (
                                <img
                                    src={newIcon?.imageUrl}
                                    className={`is-new ${newIcon?.imageType}`}
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
            )}
            <div className={`${previewBodyClass}`}>
                <div
                    className={`${previewDescriptionClass}preview-description`}
                    ref={descriptionDivRef}
                >
                    {oOfferPreviewFormatter.getPreviewDescription()}
                    <div
                        className={`description-attributes ${
                            truncatePreviewDescription ? 'truncate' : ''
                        }`}
                    >
                        {truncatePreviewDescription ? (
                            <div className="transparent-effect"></div>
                        ) : null}
                        {contentInfoDiv}
                    </div>
                </div>
                {contentAiringInfo}
                {providerImagesDiv}
                <RestrictionMessage
                    displayType={RecordingRestriction.PREVIEW_AREA}
                    preview={offerPreview}
                    channel={channel}
                />
            </div>
        </React.Fragment>
    );
};

export default PreviewPane;
